/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useCallback, useContext, useMemo } from 'react';
import { Button, Classes, Divider, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { Icon } from '/components/icon';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { WIDGETS } from 'widgets';

/**
 * Shows the view that available.
 *
 * input: [['key', 'view'], ...]
 *
 * @param views
 * @param onSelect
 * @returns {*}
 * @constructor
 */
export const SavedViewSelector = ({ views, onSelect }) => {
	return (
		<Select
			items={views}
			onItemSelect={onSelect}
			itemRenderer={ItemRenderer}
			filterable={false}
			popoverProps={{ minimal: true }}
		>
			<Button
				icon={'page-layout'}
				rightIcon={'caret-down'}
				minimal
			>
				Load View
			</Button>
		</Select>
	);
};

const ItemRenderer = (view, { handleClick, modifiers, index }) => {
	if (!modifiers.matchesPredicate) {
		return null;
	}
	return (
		<MenuItem
			key={index}
			disabled={modifiers.disabled}
			onClick={handleClick}
			text={view[1]?.metaData?.name ?? view[0]}
		/>
	);
};

// Dropdown for selecting a saved widget
export const SavedWidgetSelector = ({ cardIndex, showAllSavedWidgets }) => {
	const {
		state: {
			explorer: { selectedDefinitionName },
		},
		dispatch,
	} = useContext(WorkspaceContext);

	const { widgets } = useSavedStateSelectors();

	const savedWidgets = useMemo(() => {
		const savedWidgets = widgets
			.map((widget) => ({
				key: widget.metaData.id,
				label: widget.metaData.name,
				type: widget.type,
				icon: WIDGETS[widget.type].icon,
				savedWidget: widget,
				disabled: widget.dataConfiguration.selectedDefinition !== selectedDefinitionName,
			}))
			.sort((a, b) => (a.disabled === b.disabled ? 0 : b.disabled ? -1 : 1)); // sort disabled widgets to the bottom of the list

		return showAllSavedWidgets
			? savedWidgets.map((widget, index) => {
					if (index === 0) {
						widget.separated = true;
						return widget;
					}
					const prev = savedWidgets[index - 1];
					return {
						...widget,
						separated:
							prev.savedWidget.dataConfiguration.selectedDefinition !==
							widget.savedWidget.dataConfiguration.selectedDefinition,
					};
			  })
			: savedWidgets.filter(
					(widget) => widget?.savedWidget?.dataConfiguration?.selectedDefinition === selectedDefinitionName
			  );
	}, [selectedDefinitionName, widgets, showAllSavedWidgets]);

	const noSavedViews = savedWidgets.length === 0;

	const handleChangeWidget = useCallback(
		(widget) => {
			const newCard = {
				endpointQuery: {
					options: widget.savedWidget.dataConfiguration.endpointQuery.options,
				},
				type: widget.savedWidget.type,
				widgetConfiguration: widget.savedWidget.widgetConfiguration,
			};
			dispatch(WorkspaceActionCreator.updateExplorerCard(newCard, cardIndex));
		},
		[cardIndex, dispatch]
	);

	return (
		<Select
			items={Object.values(savedWidgets)}
			onItemSelect={handleChangeWidget}
			itemRenderer={WidgetItemRenderer}
			filterable={false}
			popoverProps={{ minimal: true }}
			disabled={noSavedViews}
			activeItem={null}
		>
			<Button disabled={noSavedViews} icon={'widget'} minimal></Button>
		</Select>
	);
};

const WidgetItemRenderer = (item, { handleClick, modifiers, index }) => {
	if (!modifiers.matchesPredicate) {
		return null;
	}

	const selectedDefinition = item.savedWidget.dataConfiguration.selectedDefinition;
	return (
		<div key={index}>
			{item.separated && (
				<React.Fragment>
					{index !== 0 && <Divider />}
					<div css={dilTitle}>{selectedDefinition}</div>
				</React.Fragment>
			)}
			<MenuItem
				active={modifiers.active}
				disabled={modifiers.disabled || item.disabled}
				onClick={handleClick}
				text={item.label}
				icon={item.icon && <Icon icon={item.icon} />}
			/>
		</div>
	);
};

const dilTitle = css`
	text-align: center;
	font-weight: bold;
`;
